<template>
  <v-dialog v-model="isOpen" max-width="800px" persistent>
    <v-card>
      <v-card-title class="text-h5 font-weight-bold">
        {{ isEditMode ? 'Edit Custom Field' : 'Add Custom Field' }}
      </v-card-title>

      <v-card-text>
        <v-form ref="formRef">
          <!-- A. Field Info Section -->
          <div class="mb-6">
            <h3 class="text-subtitle-1 font-weight-bold mb-3">Field Info</h3>

            <v-text-field
              v-model="formData.label"
              label="Friendly name *"
              placeholder="Window Sticker URL"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required]"
              @input="onLabelChange"
              class="mb-3"
            />

            <v-select
              v-model="formData.type"
              label="Type"
              :items="typeOptions"
              variant="outlined"
              density="comfortable"
              class="mb-3"
            />

            <v-checkbox v-model="formData.required" label="Required" density="compact" hide-details>
              <template v-slot:label>
                <span>Required</span>
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-icon v-bind="props" size="small" class="ml-1"
                      >mdi-information-outline</v-icon
                    >
                  </template>
                  <span>Used for validation/archiving</span>
                </v-tooltip>
              </template>
            </v-checkbox>
          </div>

          <!-- B. Sources Section -->
          <div class="mb-6">
            <div class="d-flex align-center mb-3">
              <h3 class="text-subtitle-1 font-weight-bold">Sources (ordered fallback) *</h3>
            </div>

            <div v-if="formData.sources.length === 0" class="text-error mb-3">
              At least one source is required
            </div>

            <div
              v-for="(source, index) in formData.sources"
              :key="index"
              class="mb-3 pa-3"
              style="border: 1px solid #e0e0e0; border-radius: 4px; background: #fafafa"
            >
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-2" style="cursor: grab">mdi-drag</v-icon>
                <span class="text-caption text-grey">Source {{ index + 1 }}</span>
                <v-spacer />
                <v-btn icon="mdi-delete" size="small" variant="text" @click="removeSource(index)" />
              </div>

              <v-select
                v-model="source.id"
                label="Provider *"
                :items="providerOptions"
                variant="outlined"
                density="comfortable"
                :rules="[rules.required]"
                class="mb-2"
              />

              <v-text-field
                v-model="source.field"
                label="Field name *"
                variant="outlined"
                density="comfortable"
                :rules="[rules.required]"
                class="mb-2"
              />

              <v-text-field
                v-model="source.path"
                label="Dotted path (optional)"
                placeholder="specs.engine.size"
                variant="outlined"
                density="comfortable"
              >
                <template v-slot:label>
                  <span>Dotted path (optional)</span>
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small" class="ml-1"
                        >mdi-information-outline</v-icon
                      >
                    </template>
                    <span>Use dot notation to access nested fields</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </div>

            <v-btn
              prepend-icon="mdi-plus"
              variant="outlined"
              color="primary"
              @click="addSource"
              block
            >
              Add Source
            </v-btn>
          </div>

          <!-- Advanced Options Toggle -->
          <div v-if="!isEditMode" class="mb-6">
            <v-switch
              v-model="advancedMode"
              label="Advanced"
              density="compact"
              hide-details
              color="primary"
            >
              <template v-slot:label>
                <span class="text-caption text-grey">Show advanced options</span>
              </template>
            </v-switch>
          </div>

          <!-- Field ID (Advanced) -->
          <div v-if="advancedMode && !isEditMode" class="mb-6">
            <h3 class="text-subtitle-1 font-weight-bold mb-3">Field ID</h3>
            <v-text-field
              v-model="formData.fieldId"
              label="Field ID"
              variant="outlined"
              density="comfortable"
              :rules="[rules.required, rules.fieldIdFormat, rules.fieldIdUnique]"
              @blur="onFieldIdBlur"
              hint="Manually edit the field ID (auto-generated from friendly name)"
              persistent-hint
            />
          </div>

          <!-- Indexing Section (Advanced) -->
          <div v-if="advancedMode" class="mb-4">
            <h3 class="text-subtitle-1 font-weight-bold mb-2">Indexing (optional hint)</h3>
            <p class="text-caption text-grey mb-3">
              Indexing is handled by backend templates; these are only hints.
            </p>

            <div class="d-flex flex-wrap align-center">
              <v-checkbox
                v-model="formData.indexingHint.searchable"
                label="Searchable"
                density="compact"
                hide-details
              />
              <small class="text-caption text-grey ml-8">
                Include this field in full-text search (visitors can find vehicles by this value).
              </small>
            </div>

            <div class="d-flex flex-wrap align-center">
              <v-checkbox
                v-model="formData.indexingHint.facetable"
                label="Facetable"
                density="compact"
                hide-details
              />
              <p class="text-caption text-grey ml-8">
                Allow this field to be used in filters or grouping (e.g., sidebar filters).
              </p>
            </div>

            <div class="d-flex flex-wrap align-center">
              <v-checkbox
                v-model="formData.indexingHint.sortable"
                label="Sortable"
                density="compact"
                hide-details
              />
              <p class="text-caption text-grey ml-8">
                Allow sorting results by this field (e.g., sort by price or battery size).
              </p>
            </div>
          </div>

          <!-- Behavior Section (Advanced) -->
          <div v-if="advancedMode" class="mb-6">
            <h3 class="text-subtitle-1 font-weight-bold mb-3">Behavior</h3>

            <v-select
              v-model="formData.onMissing"
              label="On missing"
              :items="onMissingOptions"
              variant="outlined"
              density="comfortable"
              class="mb-3"
            />

            <v-text-field
              v-model="formData.defaultValue"
              :label="`Default value (${formData.type})`"
              variant="outlined"
              density="comfortable"
              :type="getInputType(formData.type)"
            />
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <!-- Delete on the left (only in edit mode) -->
        <v-btn
          v-if="isEditMode"
          class="ml-1"
          color="error"
          variant="text"
          @click="handleDelete"
        >
          Delete
        </v-btn>

        <v-spacer />

        <v-btn variant="text" @click="handleCancel">Cancel</v-btn>
        <v-btn color="primary" variant="flat" @click="handleSave" :disabled="!isFormValid">
          Save Field
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'

export interface CustomFieldData {
  field: string
  custom: boolean
  label: string
  type: string
  sources: Array<{
    id: string
    field: string
    path: string | null
  }>
  required: boolean
  policy: {
    on_missing: string
  }
  default: any
  indexing_hint: {
    searchable: boolean
    facetable: boolean
    sortable: boolean
  }
}

interface Props {
  modelValue: boolean
  editData?: CustomFieldData | null
  existingFieldIds?: string[]
  normalizedFieldIds?: string[]
  availableProviders?: Array<{ id: string; friendlyName?: string }>
}

const props = withDefaults(defineProps<Props>(), {
  editData: null,
  existingFieldIds: () => [],
  normalizedFieldIds: () => [],
  availableProviders: () => [],
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'save', data: CustomFieldData): void
  (e: 'delete'): void
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const formRef = ref<any>(null)
const advancedMode = ref(false)
const isEditMode = computed(() => !!props.editData)

const formData = ref({
  label: '',
  fieldId: '',
  type: 'string',
  required: false,
  sources: [] as Array<{ id: string; field: string; path: string | null }>,
  onMissing: 'fallback',
  defaultValue: null as any,
  indexingHint: {
    searchable: false,
    facetable: false,
    sortable: false,
  },
})

const typeOptions = [
  { title: 'String', value: 'string' },
  { title: 'Number', value: 'number' },
  { title: 'Boolean', value: 'boolean' },
  { title: 'Date', value: 'date' },
]

const onMissingOptions = [
  { title: 'Fallback', value: 'fallback' },
  { title: 'Skip', value: 'skip' },
  { title: 'Error', value: 'error' },
]

const providerOptions = computed(() => {
  return props.availableProviders.map((provider) => {
    // Keep provider IDs as-is (e.g., "import_vauto") for consistent lookup
    const providerId = provider.id || ''
    return {
      title: provider.friendlyName || providerId,
      value: providerId,
    }
  })
})

// Validation rules
const rules = {
  required: (value: any) => !!value || 'Required',
  fieldIdFormat: (value: string) => {
    if (!value) return true
    const valid = /^cf_[a-z0-9_]+$/.test(value)
    return (
      valid || 'Must start with "cf_" and contain only lowercase letters, numbers, and underscores'
    )
  },
  fieldIdUnique: (value: string) => {
    if (!value) return true
    if (isEditMode.value && value === props.editData?.field) return true

    // Check against normalized fields
    if (props.normalizedFieldIds.includes(value.replace(/^cf_/, ''))) {
      return 'This ID conflicts with a normalized field'
    }

    // Check against existing custom fields
    if (props.existingFieldIds.includes(value)) {
      return 'This ID is already in use'
    }

    return true
  },
}

const handleDelete = () => {
  emit('delete')
}

const isFormValid = computed(() => {
  if (!formData.value.label || !formData.value.fieldId) return false
  if (formData.value.sources.length === 0) return false

  // Check all sources have required fields
  for (const source of formData.value.sources) {
    if (!source.id || !source.field) return false
  }

  // Check field ID format
  if (!/^cf_[a-z0-9_]+$/.test(formData.value.fieldId)) return false

  return true
})

// Generate field ID from label
const generateFieldId = (label: string): string => {
  let id = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')

  // Limit to 64 chars total (including "cf_" prefix)
  const maxLength = 61 // 64 - 3 for "cf_"
  if (id.length > maxLength) {
    id = id.substring(0, maxLength)
  }

  id = `cf_${id}`

  // Handle collisions
  let finalId = id
  let counter = 2
  while (props.existingFieldIds.includes(finalId) && !isEditMode.value) {
    finalId = `${id}_${counter}`
    counter++
  }

  return finalId
}

const onLabelChange = () => {
  if (!advancedMode.value && !isEditMode.value) {
    formData.value.fieldId = generateFieldId(formData.value.label)
  }
}

const onFieldIdBlur = () => {
  if (advancedMode.value && !formData.value.fieldId.startsWith('cf_')) {
    formData.value.fieldId = `cf_${formData.value.fieldId}`
  }

  // Validate charset: only allow [a-z0-9_]
  if (advancedMode.value) {
    formData.value.fieldId = formData.value.fieldId
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_')
  }
}

const addSource = () => {
  formData.value.sources.push({
    id: '',
    field: '',
    path: null,
  })
}

const removeSource = (index: number) => {
  formData.value.sources.splice(index, 1)
}

const getInputType = (type: string): string => {
  switch (type) {
    case 'number':
      return 'number'
    case 'date':
      return 'date'
    default:
      return 'text'
  }
}

const handleCancel = () => {
  isOpen.value = false
  resetForm()
}

const handleSave = () => {
  if (!isFormValid.value) return

  const customFieldData: CustomFieldData = {
    field: formData.value.fieldId,
    custom: true,
    label: formData.value.label,
    type: formData.value.type,
    sources: formData.value.sources.map((s) => ({
      id: s.id,
      field: s.field,
      path: s.path || null,
    })),
    required: formData.value.required,
    policy: {
      on_missing: formData.value.onMissing,
    },
    default: formData.value.defaultValue,
    indexing_hint: {
      searchable: formData.value.indexingHint.searchable,
      facetable: formData.value.indexingHint.facetable,
      sortable: formData.value.indexingHint.sortable,
    },
  }

  emit('save', customFieldData)
  isOpen.value = false
  resetForm()
}

const resetForm = () => {
  formData.value = {
    label: '',
    fieldId: '',
    type: 'string',
    required: false,
    sources: [],
    onMissing: 'fallback',
    defaultValue: null,
    indexingHint: {
      searchable: false,
      facetable: false,
      sortable: false,
    },
  }
  advancedMode.value = false
}

// Watch for edit data changes
watch(
  () => props.editData,
  (newData) => {
    if (newData) {
      formData.value = {
        label: newData.label,
        fieldId: newData.field,
        type: newData.type,
        required: newData.required,
        sources: newData.sources.map((s) => ({ ...s })),
        onMissing: newData.policy.on_missing,
        defaultValue: newData.default,
        indexingHint: { ...newData.indexing_hint },
      }
    }
  },
  { immediate: true },
)

// Watch for modal open/close
watch(isOpen, (newValue) => {
  if (!newValue) {
    resetForm()
  }
})
</script>

<style scoped>
.v-card-title {
  border-bottom: 1px solid #e0e0e0;
}
</style>
